'use client';

import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type NewsImageGalleryProps = {
  imageUrls: string[];
  title: string;
  className?: string;
};

export function NewsImageGallery({ imageUrls, title, className }: NewsImageGalleryProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const close = useCallback(() => setPreviewUrl(null), []);

  useEffect(() => {
    if (!previewUrl) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [previewUrl, close]);

  if (!imageUrls.length) return null;

  const overlay = previewUrl && mounted && typeof document !== 'undefined' && (
    createPortal(
      <button
        type="button"
        onClick={close}
        className="fixed inset-0 z-[9999] flex items-center justify-center min-w-full min-h-full w-screen h-screen bg-black/95 p-4 cursor-pointer focus:outline-none"
        style={{ inset: 0, position: 'fixed' }}
        aria-label="Закрыть"
      >
        <span className="sr-only">Закрыть (Escape)</span>
        <img
          src={previewUrl}
          alt={title}
          className="max-w-full max-h-full object-contain pointer-events-none select-none"
          draggable={false}
        />
      </button>,
      document.body
    )
  );

  return (
    <>
      <div className={className}>
        {imageUrls.map((url, idx) => (
          <button
            key={url}
            type="button"
            onClick={() => setPreviewUrl(url)}
            className="w-full text-left cursor-pointer border-0 p-0 bg-transparent rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
          >
            <img
              width={900}
              height={384}
              src={url}
              alt={imageUrls.length === 1 ? title : `${title} — изображение ${idx + 1}`}
              className="w-full h-auto max-h-96 object-contain rounded-lg transition opacity-90 hover:opacity-100"
            />
          </button>
        ))}
      </div>
      {overlay}
    </>
  );
}
